<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
	games: { type: Array, default: () => [] },
	selectGame: { type: Function, required: true },
});

const selectedCategory = ref('all');
const visibleGames = computed(() => {
	const listedGames = props.games.filter(game => !game.overview);
	return selectedCategory.value === 'all' ? listedGames : listedGames.filter(game => game.categoryId === selectedCategory.value);
});
const categories = computed(() => {
	const byId = new Map();
	props.games.forEach(game => {
		if (!game.overview && !byId.has(game.categoryId)) byId.set(game.categoryId, game);
	});
	return [...byId.values()];
});
function logoPath(file) { return `/study/img/game-logos/${file}`; }
</script>

<template>
	<header class="games-heading">
		<h1>Games</h1>
		<p>Build your design skills through quick, hands-on games and challenges.</p>
	</header>
	<div class="category-tabs" aria-label="Filter games by category">
		<button :class="['btn', { active: selectedCategory === 'all' }]" @click="selectedCategory = 'all'">All games</button>
		<button v-for="category in categories" :key="category.categoryId" :class="['btn', { active: selectedCategory === category.categoryId }]" @click="selectedCategory = category.categoryId">{{ category.category }}</button>
	</div>
	<div class="game-grid">
		<button v-for="game in visibleGames" :key="game.id" class="game-card" @click="selectGame(game.id)">
			<span class="game-logo"><img :src="logoPath(game.logo)" :alt="`${game.title} logo`" loading="lazy"></span>
			<span class="game-card-copy"><span class="game-source">{{ game.category }}</span><strong>{{ game.title }}</strong><span>{{ game.source }}</span></span>
			<i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
		</button>
	</div>
</template>

<style lang="scss" scoped>
.games-heading { margin-bottom: 24px !important; }
.games-heading h1 { margin: 4px 0; font-size: clamp(2rem, 5vw, 3rem); line-height: 1.1em; }
.games-heading p:last-child { color: var(--text2); line-height: 1.65; }
.eyebrow, .game-source { color: var(--text2); font-size: .82rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.category-tabs { display: flex; gap: 8px; overflow-x: auto; padding: 2px 0 18px; scrollbar-width: thin; }
.category-tabs .btn { flex: 0 0 auto; font-size: .9rem; }
.category-tabs .btn.active { background: var(--interactive); }
.game-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
.game-card { min-width: 0; display: flex; align-items: center; gap: 14px; text-align: left; border: 1px solid var(--border); border-radius: 14px; background: var(--bg1); color: var(--text1); padding: 12px; cursor: pointer; }
.game-card:hover { border-color: var(--text2); }
.game-logo { flex: 0 0 76px; width: 76px; height: 76px; display: grid; place-items: center; background: var(--bg2); border-radius: 10px; overflow: hidden; }
.game-logo img { width: 100%; height: 100%; object-fit: contain; }
.game-card-copy { display: flex; flex: 1; flex-direction: column; gap: 3px; min-width: 0; }
.game-card-copy strong { font-size: 1.05rem; }
.game-card-copy > span:last-child { color: var(--text2); font-size: .9rem; }
.game-card > i { color: var(--text2); }
@media (min-width: 700px) { .game-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
